import React from "react";
import { useLocation } from "react-router-dom";
import { observer } from "mobx-react-lite";
import Button from "@components/Button";
import SizedBox from "@components/SizedBox";
import Text from "@components/Text";
import AuthLayout, { ErrorAlert } from "@components/AuthLayout";
import { useStores } from "@stores";
import { useLang } from "@src/hooks/useLang";
import { authClient } from "@services/authService";
import { followOAuthRedirect, oauthRedirectHost, readOAuthQuery } from "@src/utils/oauthRequest";

type ConsentAnswer = { redirect?: boolean; url?: string };

/**
 * "Allow ChatGPT to use your GreenGrowth data?" The backend's OAuth server
 * (lib/mcpOAuth.ts) sends a signed-in user here with the signed request in the
 * query; Allow/Cancel post it back to /oauth2/consent, which answers with the
 * assistant's redirect (code on Allow, access_denied on Cancel).
 *
 * The client's name is whatever it registered with, so the redirect host from
 * the signed request (chatgpt.com, claude.ai) is shown next to it.
 */
const OAuthConsentScreen: React.FC = () => {
  const lang = useLang();
  const t = lang.aiConnector;
  const location = useLocation();
  const { accountStore } = useStores();

  const oauthQuery = React.useMemo(() => readOAuthQuery(location.search), [location.search]);
  const clientId = oauthQuery ? new URLSearchParams(oauthQuery).get("client_id") : null;
  const host = oauthQuery ? oauthRedirectHost(oauthQuery) : null;
  // farm:write lets the assistant create and change seasons, crops and
  // calibrations. A request without `scope` gets the client's registered
  // scopes, which include it (backend lib/mcpOAuth.ts, migration 0077).
  const scope = oauthQuery ? new URLSearchParams(oauthQuery).get("scope") : null;
  const canWrite = scope === null || scope.split(" ").includes("farm:write");

  const [clientName, setClientName] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!clientId || !accountStore.authenticated) return;
    let cancelled = false;
    authClient
      .$fetch<{ client_name?: string }>("/oauth2/public-client", { query: { client_id: clientId } })
      .then(({ data }) => {
        if (!cancelled && data?.client_name) setClientName(data.client_name);
      })
      .catch(() => {
        /* the host alone still says who is asking */
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, accountStore.authenticated]);

  const answer = async (accept: boolean) => {
    if (!oauthQuery) return;
    setError(null);
    setBusy(true);
    try {
      const { data, error } = await authClient.$fetch<ConsentAnswer>("/oauth2/consent", {
        method: "POST",
        body: { accept, oauth_query: oauthQuery },
      });
      if (error) {
        const code = (error as { code?: string }).code;
        if (error.status === 403) {
          setError(code === "SUBSCRIPTION_REQUIRED" ? t.consentSubscription : t.consentNotAvailable);
        } else if ((error as { error?: string }).error === "invalid_signature") {
          setError(t.requestExpired);
        } else if (error.status === 401) {
          setError(t.consentSignInAgain);
        } else {
          setError(t.consentFailed);
        }
        setBusy(false);
        return;
      }
      if (!followOAuthRedirect(data)) {
        setError(t.consentFailed);
        setBusy(false);
      }
      // On success the page navigates away; keep the buttons disabled.
    } catch {
      setError(t.consentFailed);
      setBusy(false);
    }
  };

  const name = clientName ?? t.unknownApp;
  const blocked = !oauthQuery
    ? t.requestExpired
    : accountStore.initialized && !accountStore.authenticated
      ? t.consentSignInAgain
      : null;

  return (
    <AuthLayout>
      <div style={{ width: 336 }}>
        <ErrorAlert enabled={!!(error ?? blocked)}>{error ?? blocked}</ErrorAlert>
        {!blocked && accountStore.authenticated && (
          <>
            <Text size={18} weight={500} style={{ textAlign: "center", width: "100%" }}>
              {t.consentTitle.replace("{name}", name)}
            </Text>
            <SizedBox height={16} />
            <Text size={14}>
              {(canWrite ? t.consentIntroWrite : t.consentIntro)
                .replace("{name}", name)
                .replace("{host}", host ?? "?")}
            </Text>
            <SizedBox height={8} />
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: "22px" }}>
              <li>{t.consentFields}</li>
              <li>{t.consentYield}</li>
              <li>{t.consentMachines}</li>
            </ul>
            <SizedBox height={8} />
            <Text size={14} color="#899093">
              {canWrite ? t.consentWrite : t.consentReadOnly}
            </Text>
            <SizedBox height={4} />
            <Text size={14} color="#899093">
              {t.consentRevokeHint}
            </Text>
            <SizedBox height={16} />
            {accountStore.account?.email && (
              <>
                <Text size={13} color="#899093" style={{ textAlign: "center", width: "100%" }}>
                  {t.consentSignedInAs.replace("{email}", accountStore.account.email)}
                </Text>
                <SizedBox height={16} />
              </>
            )}
            <Button onClick={() => answer(true)} disabled={busy}>
              {t.allow}
            </Button>
            <SizedBox height={8} />
            <Button kind="outline" onClick={() => answer(false)} disabled={busy}>
              {t.deny}
            </Button>
          </>
        )}
      </div>
    </AuthLayout>
  );
};

export default observer(OAuthConsentScreen);
